import React, { useContext, useEffect } from 'react'
import { Spinner, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { useMessageList, useMyNotificationCount } from 'src/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'
import ThemeContext from 'src/libraries/contexts/Theme'
import MessagesContext from '~/components/Context'
import MessageInput from '~/components/Input'
import MessageListItem from '~/components/ListItem'

const MessagesFeed = ({
   allowFileAttachments,
   allowMarkClosingMessage,
   allowMessageCreate,
   closingMessageId,
   currentUserCanModerate,
   displayMode,
   hasUnreadEntries,
   messageLastViewed,
   messageInputToolbarConfig,
   onCloseMessageButtonClick
}) => {
   const theme = useContext(ThemeContext)
   const { initiative } = useContext(InitiativeHierarchyContext)
   const { refetch: refetchNotificationCount } = useMyNotificationCount()
   const { messageList, fetchMore, loading, networkStatus } = useMessageList({
      limit: 30,
      sortBy: 'dateDesc',
      threadId: initiative.thread.id,
      type: 'human'
   })

   const handleLoadMore = () => {
      fetchMore({
         variables: {
            start: messageList.listInfo.nextCursor
         },
         updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) {
               return prevResult
            }

            return {
               ...prevResult,
               messageList: {
                  ...prevResult.messageList,
                  items: [
                     ...prevResult.messageList.items,
                     ...fetchMoreResult.messageList.items
                  ],
                  listInfo: fetchMoreResult.messageList.listInfo
               }
            }
         }
      })
   }

   // Refetch Notification Count when first viewing thread
   useEffect(() => {
      refetchNotificationCount()
   }, [initiative.id])

   const initialLoading = networkStatus <= 2
   if (initialLoading) {
      return (
         <View
            width="100%"
            textAlign="center"
            padding="30px"
            backgroundColor="#eee"
            borderRadius="6px">
            <Spinner color="#ccc" width="24px" height="24px" />
         </View>
      )
   }

   // Message List Items - Reverse order if sorted by desc or asc order
   const messageListItems = displayMode === 'feed'
      ? messageList.items
      : [...messageList.items].reverse()

   return (
      <MessagesContext.Provider
         editorAllowFileAttachments={allowFileAttachments}
         editorInputToolbarConfig={messageInputToolbarConfig}
         initiativeId={initiative.id}>
         {allowMessageCreate && displayMode === 'feed' && (
            <MessageInput
               allowFileAttachments={allowFileAttachments}
               inputToolbarConfig={messageInputToolbarConfig}
               threadTargetId={initiative.id}
               threadTargetType="initiative"
            />
         )}
         {messageList.items.length === 0 && (
            <View float="left" width="100%" padding="30px 0px" textAlign="center">
               <Text size="2" color="#777">
                  No comments have been posted yet
               </Text>
            </View>
         )}
         {displayMode === 'conversation' && messageList.listInfo.hasNext && (
            <View
               onClick={handleLoadMore}
               float="left"
               width="100%"
               borderBottom="1px solid #eee"
               padding="20px 0px"
               style={{ cursor: 'pointer' }}>
               <Text size="1.5" weight="medium" color="#888">
                  {loading ? 'Loading...' : 'View earlier comments...'}
               </Text>
            </View>
         )}
         {messageListItems.map((message, index) => (
            <MessageListItem
               key={message.id}
               id={message.id}
               allowMarkClosingMessage={allowMarkClosingMessage}
               allowMessageCreate={allowMessageCreate}
               anon={message.anon}
               authorAvatarUrl={message?.author?.avatarMedia?.file.thumbnailUrlW48}
               authorBadges={message?.author?.badges}
               authorId={message?.author?.id}
               authorName={message?.author?.fullName}
               body={message.body}
               borderStyle={index === 0 ? 'none' : undefined}
               currentUserCanModerate={currentUserCanModerate}
               edited={message.edited}
               dateAdded={message.dateAdded}
               flaggedByMe={message.flaggedByMe}
               isClosingMessage={closingMessageId === message.id}
               isLikedByMe={message.isLikedByMe}
               isMine={message.isMine}
               numLikes={message.numLikes}
               onCloseMessageButtonClick={() => onCloseMessageButtonClick(message.id)}
               replies={message.replies}
               showNewMessageAnchor={hasUnreadEntries && messageLastViewed === message.id}
               textColor={theme.tmpBodyTextColor}
               textSize={theme.tmpBodyTextSize}
               textLineHeight={theme.tmpBodyTextLineHeight}
            />
         ))}
         {displayMode === 'feed' && messageList.listInfo.hasNext && (
            <View
               onClick={handleLoadMore}
               float="left"
               width="100%"
               borderTop="1px solid #eee"
               padding="20px 0px"
               style={{ cursor: 'pointer' }}>
               <Text size="1.5" weight="medium" color="#888">
                  {loading ? 'Loading...' : 'View earlier comments...'}
               </Text>
            </View>
         )}
         {allowMessageCreate && displayMode === 'conversation' && (
            <View float="left" width="100%">
               <MessageInput
                  allowFileAttachments={allowFileAttachments}
                  inputToolbarConfig={messageInputToolbarConfig}
                  threadTargetId={initiative.id}
                  threadTargetType="initiative"
               />
            </View>
         )}
      </MessagesContext.Provider>
   )
}

MessagesFeed.propTypes = {
   allowFileAttachments: PropTypes.bool,
   allowMarkClosingMessage: PropTypes.bool,
   allowMessageCreate: PropTypes.bool,
   closingMessageId: PropTypes.string,
   currentUserCanModerate: PropTypes.bool.isRequired,
   // Coversation display mode shows messages with the newest at the bottom
   // Feed display mode shows newest messages at the top
   displayMode: PropTypes.oneOf(['conversation', 'feed']).isRequired,
   hasUnreadEntries: PropTypes.bool,
   messageLastViewed: PropTypes.string,
   messageInputToolbarConfig: PropTypes.oneOf(['advanced', 'textBasic']),
   onCloseMessageButtonClick: PropTypes.func
}

MessagesFeed.defaultProps = {
   allowFileAttachments: false,
   allowMarkClosingMessage: false,
   allowMessageCreate: true,
   closingMessageId: undefined,
   hasUnreadEntries: false,
   messageLastViewed: undefined,
   messageInputToolbarConfig: 'textBasic',
   onCloseMessageButtonClick: undefined
}

export default MessagesFeed
